import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Loader2, Mail } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { useAuthStore } from "../store/authStore";
import { toast } from "../components/ui/Toaster";

const ForgotPasswordPage: React.FC = () => {
  const [email, setEmail] = useState("");
  const [emailSent, setEmailSent] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const { forgotPassword } = useAuth();
  const { isLoading } = useAuthStore();

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!email.trim()) {
      newErrors.email = "E-mail is vereist";
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      newErrors.email = "Voer een geldig e-mailadres in";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      await forgotPassword(email);
      setEmailSent(true);
      toast.success(
        "E-mail verstuurd!",
        "Controleer je inbox voor wachtwoord reset instructies",
      );
    } catch (error: any) {
      console.error("Forgot password failed:", error);
      toast.error("Versturen email mislukt", error.message || "Probeer opnieuw");
    }
  };

  if (emailSent) {
    return (
      <div className="space-y-6">
        <div className="text-center">
          <div className="mx-auto flex items-center justify-center h-14 w-14 rounded-full border border-neon-lime/25 bg-neon-lime/[.08]">
            <Mail className="h-7 w-7 text-neon-lime" />
          </div>
          <h2 className="mt-4 text-2xl font-bold text-fg-bright tracking-tight">
            Controleer je email
          </h2>
          <p className="mt-2 text-sm text-fg-muted">
            We hebben wachtwoord reset instructies verzonden naar{" "}
            <strong className="text-fg-soft">{email}</strong>
          </p>
        </div>

        <div className="text-center">
          <p className="text-sm text-fg-muted">
            E-mail niet ontvangen? Controleer je spam-map of{" "}
            <button
              onClick={() => setEmailSent(false)}
              className="sf-link"
            >
              probeer opnieuw
            </button>
          </p>
        </div>

        <div className="text-center">
          <Link
            to="/login"
            className="sf-link inline-flex items-center text-sm"
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Terug naar inloggen
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="text-center">
        <div className="sf-eyebrow mb-3">// wachtwoord vergeten</div>
        <h2 className="text-2xl font-bold text-fg-bright tracking-tight">
          Wachtwoord vergeten?
        </h2>
        <p className="mt-2 text-sm text-fg-muted">
          Voer je e-mailadres in en we sturen je een link om je wachtwoord
          opnieuw in te stellen.
        </p>
      </div>

      <form className="space-y-4" onSubmit={handleSubmit}>
        <div>
          <label
            htmlFor="email"
            className="block text-sm font-semibold text-fg-soft"
          >
            E-mailadres
          </label>
          <div className="mt-1.5">
            <input
              id="email"
              name="email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (errors.email) {
                  setErrors((prev) => ({ ...prev, email: "" }));
                }
              }}
              className={`sf-input ${
                errors.email ? "!border-neon-pink/60" : ""
              }`}
              placeholder="Voer je e-mailadres in"
            />
            {errors.email && (
              <p className="mt-1 text-sm text-neon-pink-soft">{errors.email}</p>
            )}
          </div>
        </div>

        <div>
          <button
            type="submit"
            disabled={isLoading}
            className="sf-btn-primary w-full py-3.5 text-base"
          >
            {isLoading ? (
              <>
                <Loader2 className="animate-spin -ml-1 mr-2 h-4 w-4" />
                Verzenden...
              </>
            ) : (
              "Verstuur reset link"
            )}
          </button>
        </div>

        <div className="text-center">
          <Link
            to="/login"
            className="sf-link inline-flex items-center text-sm"
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Terug naar inloggen
          </Link>
        </div>
      </form>
    </div>
  );
};

export default ForgotPasswordPage;
